import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";

import { displayDate, getLocalSystemDate, formatDateForInput } from "../../../utils/dateUtils";
import { FORM_FIELD } from "../../../ui/formTheme";
import YearJumpPopover from "../../../components/datePicker/YearJumpPopover";
import { nativeDatePickerSupportsYearJump } from "../../../components/datePicker/nativeYearJump";

const supportsNativeYearJump = nativeDatePickerSupportsYearJump();

const InputDatePicker = ({
    label,
    name,
    labelClassName = FORM_FIELD.label,
    inputClassName = FORM_FIELD.control,
    isRequired = false,
    readOnly = false,
    disabled = false,
    formatSeparator = "/",
    allowManualEntry = false,
    compact = false,
    reserveErrorSpace = false,
    errorClassName = FORM_FIELD.errorText,
    allowFuture = false,
    max,
    min,
    ...props
}) => {
    // Compact fits the picker into dense form rows without the icon crowding
    // the value.
    const iconSizeClass = compact ? "h-3.5 w-3.5" : "h-5 w-5";
    const iconPositionClass = compact ? "right-1" : "right-3";
    const inputPaddingClass = compact ? "pr-5" : "pr-10";
    const formContext = useFormContext();
    const dateRef = useRef(null);
    const textRef = useRef(null);
    const fieldRef = useRef(null);
    const suppressOpenRef = useRef(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    const { register, watch, formState: { errors } } = formContext || {
        register: () => ({}),
        watch: () => props.value,
        formState: { errors: props.errors || {} }
    };

    const rhfValue = watch ? watch(name) : props.value;

    const [localText, setLocalText] = useState("");

    // Sync external RHF value to local text only when it mounts or external value changes outside of our local text mapping
    useEffect(() => {
        let currentDisplay = displayDate(rhfValue);
        if (currentDisplay && formatSeparator !== "-") {
             currentDisplay = currentDisplay.replace(/-/g, formatSeparator);
        }
        if (rhfValue && currentDisplay) {
            const localYmd = localTextToYMD(localText);
            const ymdFromRhf = formatDateForInput(rhfValue);
            const rhfMatches =
                localYmd === rhfValue ||
                (ymdFromRhf && localYmd === ymdFromRhf);
            const needsSeparatorFix =
                formatSeparator !== "-" &&
                localText &&
                localText.includes("-") &&
                rhfMatches;
            if (localYmd !== rhfValue || needsSeparatorFix) {
                setLocalText(currentDisplay);
            }
        } else if (!rhfValue && localTextToYMD(localText)) {
            setLocalText("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rhfValue, formatSeparator]);

    const getNestedError = (obj, path) => {
        if (!obj || !path) return null;
        return path.split(".").reduce((acc, part) => acc && acc[part], obj);
    };

    const error = getNestedError(errors, name);
    const today = getLocalSystemDate();

    const ymdValue = formatDateForInput(rhfValue);

    const rhfProps = register(name, props.rules);

    function localTextToYMD(text) {
        if (!text) return "";
        const m = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
        if (m) {
            return `${m[3]}-${m[1]}-${m[2]}`;
        }
        return "";
    }

    const handleTextChange = (e) => {
        let val = e.target.value;
        val = val.replace(/[^0-9\-/]/g, "");

        if (val.length === 2 && localText.length === 1 && !val.includes(formatSeparator)) {
            val += formatSeparator;
        } else if (val.length === 5 && localText.length === 4 && val.split(formatSeparator).length === 2) {
            val += formatSeparator;
        }

        setLocalText(val);

        const ymd = localTextToYMD(val);
        if (ymd) {
            if (rhfProps.onChange) rhfProps.onChange({ target: { name, value: ymd } });
            if (props.onChange) props.onChange({ target: { name, value: ymd } });
        } else if (val === "") {
            if (rhfProps.onChange) rhfProps.onChange({ target: { name, value: "" } });
            if (props.onChange) props.onChange({ target: { name, value: "" } });
        }
    };

    const handleBlur = (e) => {
        if (rhfProps.onBlur) rhfProps.onBlur(e);

        if (localText) {
            if (formatSeparator === "-") {
                setLocalText(localText.replace(/\//g, "-"));
            } else if (formatSeparator === "/") {
                setLocalText(localText.replace(/-/g, "/"));
            }
        }
    };

    const handleDateChange = (e) => {
        const ymd = e.target.value;
        if (ymd) {
            let displayVal = displayDate(ymd);
            if (displayVal && formatSeparator !== "-") {
                displayVal = displayVal.replace(/-/g, formatSeparator);
            }
            setLocalText(displayVal);
            if (rhfProps.onChange) rhfProps.onChange({ target: { name, value: ymd } });
            if (props.onChange) props.onChange({ target: { name, value: ymd } });
        } else {
            setLocalText("");
            if (rhfProps.onChange) rhfProps.onChange({ target: { name, value: "" } });
            if (props.onChange) props.onChange({ target: { name, value: "" } });
        }
        if (!supportsNativeYearJump) suppressOpenRef.current = true;
        if (textRef.current) textRef.current.focus();
    };

    const openPicker = () => {
        if (disabled) return;
        if (!supportsNativeYearJump) {
            setPickerOpen(true);
            return;
        }
        if (dateRef.current && typeof dateRef.current.showPicker === "function") {
            try {
                dateRef.current.showPicker();
            } catch (err) {
                dateRef.current.focus();
            }
        } else if (dateRef.current) {
            dateRef.current.focus();
        }
    };

    const handleTextFocus = (e) => {
        if (props.onFocus) props.onFocus(e);
        if (suppressOpenRef.current) {
            suppressOpenRef.current = false;
            return;
        }
        if (!allowManualEntry) openPicker();
    };

    const blockManualInputPaste = (e) => {
        if (!allowManualEntry) e.preventDefault();
    };

    return (
        <div className={`${props.className || "w-full"} min-w-0 max-w-full`}>
            {label && (
                <label className={labelClassName}>
                    {label} {isRequired && <span className="text-red-500">*</span>}
                </label>
            )}

            {readOnly ? (
                <div className={`${inputClassName} ${FORM_FIELD.disabled}`}>
                    {localText || (displayDate(rhfValue) ? displayDate(rhfValue).replace(/-/g, formatSeparator) : "") || "N/A"}
                </div>
            ) : (
                <div className="relative flex w-full min-w-0 items-center" ref={fieldRef}>
                    <input
                        {...rhfProps} // this sets name, ref (overridden below), onChange, onBlur
                        {...props}
                        type="text"
                        ref={(e) => {
                            if (typeof rhfProps.ref === 'function') rhfProps.ref(e);
                            textRef.current = e;
                        }}
                        value={localText}
                        onChange={handleTextChange}
                        onBlur={handleBlur}
                        onFocus={handleTextFocus}
                        onPaste={blockManualInputPaste}
                        onDrop={blockManualInputPaste}
                        placeholder={`MM${formatSeparator}DD${formatSeparator}YYYY`}
                        maxLength={10}
                        disabled={disabled}
                        readOnly={!allowManualEntry}
                        className={
                            compact
                                ? // Dense clinical rows pass the full look via inputClassName —
                                  // do not merge FORM_FIELD.control (text-sm / py-2.5) or size fights.
                                  `w-full min-w-0 max-w-full ${inputClassName} ${inputPaddingClass} ${error ? FORM_FIELD.error : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}${!allowManualEntry ? " cursor-pointer" : ""}`
                                : `${FORM_FIELD.control} w-full min-w-0 max-w-full ${inputClassName} ${inputPaddingClass} ${error ? FORM_FIELD.error : ""} ${disabled ? FORM_FIELD.disabled : `${FORM_FIELD.enabled}${!allowManualEntry ? " cursor-pointer" : ""}`}`
                        }
                    />

                    {/* Hidden Date Input triggered by Calendar Icon — sized to the field so the native picker is not clipped. */}
                    <input
                        type="date"
                        ref={dateRef}
                        value={ymdValue}
                        onChange={handleDateChange}
                        min={min}
                        max={max !== undefined ? max : (allowFuture ? undefined : today)}
                        tabIndex="-1"
                        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                    />

                    {!supportsNativeYearJump && (
                        <YearJumpPopover
                            open={pickerOpen}
                            anchorRef={fieldRef}
                            value={ymdValue}
                            min={min}
                            max={max !== undefined ? max : (allowFuture ? undefined : today)}
                            today={today}
                            onClose={() => setPickerOpen(false)}
                            onSelect={(ymd) => {
                                handleDateChange({ target: { value: ymd } });
                                setPickerOpen(false);
                            }}
                        />
                    )}

                    <button
                        type="button"
                        disabled={disabled}
                        tabIndex="-1" // prevent tab stop on icon
                        onClick={openPicker}
                        className={`absolute ${iconPositionClass} top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Open calendar"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={iconSizeClass} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}

            {reserveErrorSpace ? (
                <p
                    className={`${errorClassName} min-h-[14px] leading-tight ${error?.message ? "" : "invisible"}`}
                    aria-live="polite"
                >
                    {error?.message || "\u00a0"}
                </p>
            ) : error ? (
                <p className={errorClassName}>{error.message}</p>
            ) : null}
        </div>
    );
};

InputDatePicker.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    labelClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    isRequired: PropTypes.bool,
    readOnly: PropTypes.bool,
    /** false = picker only; true (default) = user may type the date */
    allowManualEntry: PropTypes.bool,
    /** Shrinks the calendar icon so the field fits dense rows. */
    compact: PropTypes.bool,
    reserveErrorSpace: PropTypes.bool,
    errorClassName: PropTypes.string,
    disabled: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    onChange: PropTypes.func,
    errors: PropTypes.object,
    className: PropTypes.string,
    max: PropTypes.string,
    min: PropTypes.string,
    allowFuture: PropTypes.bool,
};

export default InputDatePicker;
